/**
 * 30. Tax Dashboard — tax payable and receivable, and the tax returns
 * recorded for each period.
 *
 * Never an untrue figure (REP-TAXDASH, D663; owner rule "never hide
 * anything"). `GET /v1/finance/tax/dashboard` answers four figures that cannot
 * be right yet, and this page shows none of them:
 *
 * - `payable` and `receivable` are sums over `TaxTransaction`, which no
 *   posting writes yet, so both read 0 whatever tax has been posted. Each
 *   card says the figure is not available yet and names TAX-P0, the slice
 *   that fills the tax ledger and makes the dashboard read it (tax-p0.md
 *   P0-3 and P0-6, which rebuild this screen with per-entity rows).
 * - each return's `amount` is the literal "0" and its `dueDate` is the day
 *   the record was created. The table shows what the API really knows (the
 *   period, the tax type and the status) and a note above it says the due
 *   date and the amount are not shown yet, naming T11 (the VAT return).
 *
 * Those four fields are left out of the type below on purpose, so nothing on
 * this screen can render them; the API half of D663 stays open for the
 * slices that make them true.
 *
 * Arabic and English (C7): every word from the catalogs; a tax type and a
 * return's status are words, never the stored value; the slice ids are
 * isolated left to right inside an Arabic sentence (PendingAction.tsx).
 */
import {
  Card,
  PageHeader,
  Skeleton,
  DataTable,
  Badge,
  StatCard,
} from "@/components/ui";
import { QueryError } from "@/components/QueryError";
import { usePendingReason } from "@/components/PendingAction";
import { useApiQuery } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { statusLabel, valueLabel } from "@/lib/i18n/records";

/**
 * What this screen reads of the answer. The API also sends `payable`,
 * `receivable`, and each return's `dueDate` and `amount`; none is true yet
 * (D663), so none is read.
 */
interface TaxDashboardData {
  returns: Array<{
    id: string;
    period: string;
    taxType: string;
    status: "Draft" | "Filed" | "Overdue" | "Paid";
  }>;
}

/** The slice that makes the payable and receivable totals true. */
const TOTALS_SLICE = "TAX-P0";
/** The slice that works out a return's due date and amount. */
const RETURN_FIGURES_SLICE = "T11";

/** A total that cannot be right yet: its name, the state, and why. */
function TotalNotAvailable({ label }: { label: string }) {
  const { t } = useTranslation();
  const why = usePendingReason("taxLedger", TOTALS_SLICE);
  return (
    <StatCard
      label={label}
      value={t("notAvailable.title")}
      detail={
        <>
          <p style={{ marginBlock: "8px 4px" }}>
            {t("finance.tax.totalNotShown")}
          </p>
          <p style={{ margin: 0 }}>{why}</p>
        </>
      }
    />
  );
}

export function TaxDashboardPage() {
  const { t } = useTranslation();
  const returnFiguresWhy = usePendingReason("vatReturn", RETURN_FIGURES_SLICE);
  const { data, isPending, error, refetch, isFetching } =
    useApiQuery<TaxDashboardData>(["finance", "tax"], "/finance/tax/dashboard");

  if (isPending) return <Skeleton rows={10} />;
  if (error || !data)
    return (
      <QueryError
        error={error}
        retrying={isFetching}
        onRetry={() => void refetch()}
      />
    );

  return (
    <div>
      <PageHeader
        title={t("finance.tax.title")}
        subtitle={t("finance.tax.subtitle")}
      />

      <div className="atlas-grid atlas-grid--2 mb-4">
        <TotalNotAvailable label={t("finance.tax.payable")} />
        <TotalNotAvailable label={t("finance.tax.receivable")} />
      </div>

      <Card title={t("finance.tax.returns")}>
        <p style={{ marginBlock: "0 4px" }}>
          {t("finance.tax.returnFiguresNotShown")}
        </p>
        <p style={{ marginBlock: "0 12px" }}>{returnFiguresWhy}</p>
        <DataTable
          rows={data.returns}
          columns={[
            { key: "period", label: t("fields.period") },
            {
              key: "taxType",
              label: t("fields.type"),
              render: (r) => valueLabel(t, "taxType", r.taxType),
            },
            {
              key: "status",
              label: t("fields.status"),
              render: (r) => (
                <Badge
                  tone={
                    r.status === "Overdue"
                      ? "danger"
                      : r.status === "Filed"
                        ? "success"
                        : "warning"
                  }
                >
                  {statusLabel(t, "TaxReturn", r.status)}
                </Badge>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
